import PropTypes from 'prop-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { Pencil, Trash2 } from 'lucide-react';

import { deleteCostEstimate } from '#/api/crmMgmt/costEstimate/delete';
import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import {
  getCostEstimateApiErrorMessage,
  isCostEstimateVersionConflict,
} from '#/modules/crmMgmt/costEstimate/helper/apiError';
import { DESTRUCTIVE_SOLID_BUTTON_CLASS } from '#/modules/imsMgmt/receipts/shared/destructiveSolidButtonClass';
import { useCan } from '#/modules/infra/auth/useCan';
import { costEstimateListQueryKeyPrefix } from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

/** 成本試算指令：編輯、刪除（皆需 crm.cost_estimate.update；沒有狀態）。 */
const CostEstimateCommandBar = ({ detail, onRefetch }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const canUpdate = useCan(PERM.COST_ESTIMATE_UPDATE);

  const { id } = detail;

  const deleteMutation = useMutation({
    retry: false,
    mutationFn: () => deleteCostEstimate(id, { updatedAt: detail.record?.updatedAt }),
    onSuccess: async (result) => {
      toast.success(result?.message || '已刪除');
      await queryClient.invalidateQueries({ queryKey: costEstimateListQueryKeyPrefix() });
      navigate(ROUTES.costEstimate);
    },
    onError: async (error) => {
      toast.error(getCostEstimateApiErrorMessage(error));
      if (isCostEstimateVersionConflict(error)) {
        await onRefetch?.();
      }
    },
  });

  async function handleDelete() {
    const ok = await confirm({
      title: '刪除成本試算',
      message: '將刪除此成本試算（不可復原）。確定刪除？',
      confirmText: '刪除',
      destructive: true,
    });
    if (ok) {
      deleteMutation.mutate();
    }
  }

  if (!canUpdate) {
    return null;
  }

  return (
    <>
      {ConfirmDialogHost}
      <Button asChild className="document-detail-edit-action">
        <Link to={ROUTES.costEstimateEdit(id)}>
          <Pencil className="size-3.5" aria-hidden />
          編輯
        </Link>
      </Button>
      <Button
        type="button"
        variant="destructive"
        className={DESTRUCTIVE_SOLID_BUTTON_CLASS}
        disabled={deleteMutation.isPending}
        onClick={handleDelete}
      >
        <Trash2 className="size-3.5" aria-hidden />
        刪除
      </Button>
    </>
  );
};

CostEstimateCommandBar.propTypes = {
  detail: PropTypes.object.isRequired,
  onRefetch: PropTypes.func,
};

export default CostEstimateCommandBar;
